/* „Co się zmieniło między A i B" — the diverging-bars table both two-dates tabs draw. Forma draws
 * it `layout="compact"`: four short figures in a narrower centre column. */

/* The third answer where the eleven rows would be: the read failed. One line inside the reserve the
 * rows already held, rather than eleven rows of „bez pary" claiming a comparison nobody made. */
.dvNotice {
  display: grid;
  flex: 1;
  place-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* Title and caption INLINE, 10px apart, both on the left. Not `.cardHead`, which pushes its aside
 * to the far end — right for a card whose aside is a count, wrong for a caption that explains the
 * bars under it. */
.tableHead {
  display: flex;
  gap: var(--space-10px);
  align-items: baseline;
  min-width: 0;
}

.tableTitle {
  flex: none;
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
}

.tableCaption {
  overflow: hidden;
  font-size: var(--fs-label);
  font-style: normal;
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The canon's five tracks: the quantity, A, the shared bar track, B, and the change. The two
 * figures are fixed so the bars' centre line is the same pixel on every row. */
.dvHead,
.dvRow {
  display: grid;

  /* The figure and change columns are narrower on Forma, whose four numbers are short; the card
   * that holds the table sets `--dv-label`, `--dv-figure` and `--dv-change`. */
  grid-template-columns:
    minmax(0, var(--dv-label, var(--w-dv-label))) var(--dv-figure, var(--w-dv-figure)) minmax(0, 1fr)
    var(--dv-figure, var(--w-dv-figure)) var(--dv-change, var(--w-dv-change));
  gap: var(--space-3);
  align-items: center;
}

.dvHead {
  padding-bottom: var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  border-bottom: var(--border-hairline) solid var(--border);
}

.dvNumHead {
  text-align: right;
}

.dvBarHead {
  text-align: center;
}

.dvTable {
  display: flex;
  flex-direction: column;
}

/* Every row, reserved before any of them has a number in it. `--dv-row-count` is the table's own
 * skeleton count. */
.dvRows {
  display: flex;
  flex-direction: column;
  min-height: calc(var(--dv-row-count) * var(--h-dv-row) + var(--h-two-dates-rows-extra));
}

.dvRow {
  height: var(--h-dv-row);
  border-bottom: var(--border-hairline) solid var(--border);
}

.dvLabel {
  overflow: hidden;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dvNum {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* B reads from the left of its own column, against the bar rather than against the card's edge. */
.dvNumB {
  text-align: left;
}

/* The date a STALE reading actually came from, under the value it stands for. */
.dvStale {
  display: block;
  font-size: var(--fs-dv-stale);
  font-weight: var(--fw-medium);
  line-height: var(--lh-tight);
  color: var(--text-2);
}

/* The shared track. The centre line runs the full height of the row so the eleven zeros read as one
 * axis rather than as eleven ticks. */
.dvBar {
  position: relative;
  height: var(--h-dv-bar-track);
}

/* The zero axis. It runs twelve pixels past the track on each side, which is exactly the space
 * between two 38px rows holding a 14px track — so the eleven segments meet and the line reads as
 * ONE unbroken axis down the whole table body rather than as eleven ticks. */
.dvBar::before {
  position: absolute;
  inset-block: calc(var(--space-3) * -1);
  inset-inline-start: 50%;
  width: var(--border-hairline);
  content: '';
  background: var(--border-2);
}

.dvFill {
  position: absolute;
  inset-block-start: var(--space-2px);
  height: var(--h-dv-bar);
  background: var(--text-2);
  border-radius: var(--radius-bar);
}

/* Green only where LOWER is the goal — waist, hips, body fat, neck. Weight is never coloured: a
 * client on a bulk and a client on a cut want opposite directions of the same number. */
.dvFillGood {
  background: var(--good-ink);
}

/* The bar's own percentage, standing just past the end of the bar. Its POSITION is data — a
 * percentage of the track — so it can only be an inline style; the gap between the bar's end and
 * the first glyph is a design value and is declared here, read from the inline `calc()` as a custom
 * property so no pixel literal reaches a `.tsx`. */
.dvPercent {
  --dv-percent-gap: var(--space-6px);

  position: absolute;
  inset-block-start: 0;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--h-dv-bar-track);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.dvChange {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.dvChangeGood {
  color: var(--good-ink);
}

/* A quantity with no reading on one of the two dates. Said in words, because a dash reads as zero. */
.dvNoPair {
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.skelDvLabel {
  width: 70%;
  height: var(--fs-note);
}

.skelDvNum {
  justify-self: end;
  width: 46px;
  height: var(--fs-note);
}

/* Forma: the head may wrap under a long caption, the bar column's heading holds one line, B is
 * right-aligned so a percentage past a bar keeps its room in the narrower track, and the reserve is
 * the rows alone. */
.compact.tableHead {
  flex-wrap: wrap;
}

/* Forma's heading names A's and B's dates, so its width moves with the markers. The caption takes
 * a line of its own rather than wrapping under the heading only when the dates are long, so the
 * head is the same height waiting, loaded and on every pair of dates. */
.compact .tableCaption {
  flex-basis: 100%;
}

.compact .dvBarHead {
  white-space: nowrap;
}

.compact .dvNumB,
.compact .dvHeadB {
  text-align: right;
}

.compact .dvRows {
  min-height: calc(var(--dv-row-count) * var(--h-dv-row));
}
